import { useQuery } from 'react-query';
import api from 'api';
import { GenericDeclaration } from 'components/ui/DeclarationWindow/types';
import {
  parseSettings,
  parseListEffectiveSettings,
} from 'components/Settings/FlexibleBenefitsSettings/utils';
import type { UseFlexibleBenefitsWindowStatusParams, FlexibleBenefitsWindowStatus } from './types';

const isWindowCurrentlyOpen = (windowConfig?: GenericDeclaration): boolean => {
  if (!windowConfig) {
    return false;
  }

  const now = new Date();

  switch (windowConfig.type) {
    case 'always':
      return true;

    case 'monthly': {
      const currentDayOfMonth = now.getDate();
      return (
        currentDayOfMonth >= windowConfig.startDate && currentDayOfMonth <= windowConfig.endDate
      );
    }

    case 'custom': {
      const currentTime = now.getTime();
      return Object.values(windowConfig.dates).some(({ startDate, endDate }) => {
        if (!startDate || !endDate) return false;
        return currentTime >= startDate.getTime() && currentTime <= endDate.getTime();
      });
    }

    default:
      return false;
  }
};

const useFlexibleBenefitsWindowStatus = ({
  orgId,
  isM3S2Enabled,
  isFBPDisabled,
  onError,
}: UseFlexibleBenefitsWindowStatusParams): FlexibleBenefitsWindowStatus => {
  // Fetch flexible benefits settings (new flow - M3S2)
  const { data: newFlowData, isLoading: isNewFlowLoading } = useQuery({
    queryKey: ['flexibleBenefitsEffectiveSettings', orgId],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.listEffectiveSettings({
        page: 0,
        page_size: 100,
      }),
    enabled: isM3S2Enabled,
    refetchOnWindowFocus: false,
    onError,
  });

  // Fetch flexible benefits settings (old flow)
  const { data: oldFlowData, isLoading: isOldFlowLoading } = useQuery({
    queryKey: ['flexibleBenefitsSettings', orgId],
    queryFn: () => api.flexibleBenefits.getSettings(orgId),
    enabled: !isM3S2Enabled && !isFBPDisabled,
    refetchOnWindowFocus: false,
    onError,
  });

  const isLoading = isM3S2Enabled ? isNewFlowLoading : isOldFlowLoading;

  let isFBPSettingsEnabled = false;
  if (isM3S2Enabled && newFlowData?.payrollListEffectiveSettings?.settings) {
    isFBPSettingsEnabled = newFlowData.payrollListEffectiveSettings.settings.length > 0;
  } else if (!isM3S2Enabled && !isFBPDisabled && oldFlowData) {
    isFBPSettingsEnabled = oldFlowData.employeeCanDeclareFlexiBenefits === true;
  }

  let declarationWindow: GenericDeclaration | undefined;
  let proofUploadWindow: GenericDeclaration | undefined;

  if (isM3S2Enabled && newFlowData?.payrollListEffectiveSettings?.settings) {
    const parsedSettings = parseListEffectiveSettings(
      newFlowData.payrollListEffectiveSettings.settings,
    );
    declarationWindow = parsedSettings.employeeDeclarationWindow;
    proofUploadWindow = parsedSettings.employeeProofUploadWindow;
  } else if (!isM3S2Enabled && !isFBPDisabled && oldFlowData) {
    const parsedSettings = parseSettings(oldFlowData);
    declarationWindow = parsedSettings.employeeDeclarationWindow;
    proofUploadWindow = parsedSettings.employeeProofUploadWindow;
  }

  const isDeclarationWindowOpen = isWindowCurrentlyOpen(declarationWindow);
  const isProofUploadWindowOpen = isWindowCurrentlyOpen(proofUploadWindow);

  return {
    isLoading,
    isFBPSettingsEnabled,
    isDeclarationWindowOpen,
    isProofUploadWindowOpen,
  };
};

export { useFlexibleBenefitsWindowStatus, isWindowCurrentlyOpen };
